import { Deferred, Head, Link, router, usePage } from "@inertiajs/react";
import type { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { useLocalStorage } from "@uidotdev/usehooks";
import { format, formatDistanceToNow } from "date-fns";
import { id } from "date-fns/locale";
import { AnimatePresence, motion } from "framer-motion";
import type { TFunction } from "i18next";
import {
	Ban,
	CalendarClock,
	CheckIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	ClockIcon,
	Handshake,
	ListFilterIcon,
	MoreHorizontal,
	NotepadTextDashedIcon,
	Package,
	PencilIcon,
	PlusIcon,
	Trash2,
	User,
	UserPenIcon,
	UsersIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ApptViewChanger from "@/components/admin-portal/appointment/appt-view-changer";
import {
	ChatCopyMenu,
	DRAFT_CHAT_COPY_TEMPLATES,
} from "@/components/admin-portal/appointment/chat-copy-menu";
import DraftFilterList from "@/components/admin-portal/appointment/draft-filter-list";
import {
	EMPTY_PIC_SLOTS,
	holdsPicSlot,
	OTHER_PIC_ROLE,
	PIC_SLOT_KEYS,
	PicSlotSelectors,
	type PicSlots,
	picRoleRank,
} from "@/components/admin-portal/appointment/form/pic-slot-selectors";
import {
	MitraAssignmentReasonDialog,
	type MitraAssignmentReasonTarget,
} from "@/components/admin-portal/appointment/mitra-assignment-reason";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import PasteWaFormButton from "@/components/admin-portal/appointment/paste-wa-form-button";
import { PreferredSchedules } from "@/components/admin-portal/appointment/preferred-schedules";
import {
	type RowTag,
	RowTags,
} from "@/components/admin-portal/appointment/row-tags";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { useDateContext } from "@/components/providers/date-provider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
	MultiSelector,
	MultiSelectorContent,
	MultiSelectorInput,
	MultiSelectorItem,
	MultiSelectorList,
	MultiSelectorTrigger,
} from "@/components/ui/multi-select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { DRAFT_STEPS_LABELS } from "@/hooks/admin-portal/appointment/use-appointment-draft";
import { useIsMobile } from "@/hooks/use-mobile";
import { type DraftVisit, draftVisits } from "@/lib/appointments/draft-visits";
import { preferredSchedules } from "@/lib/appointments/preferred-schedules";
import {
	findReasonLabel,
	type ReasonOption,
	reasonLabel,
} from "@/lib/appointments/reason-label";
import { DEFAULT_TIME_FORMAT_24_DATE_FNS } from "@/lib/constants";
import { getCsrfToken } from "@/lib/csrf";
import { cn, populateQueryParams, removeWhiteSpaces } from "@/lib/utils";
import type { Admin } from "@/types/admin-portal/admin";
import type { AppointmentDraft } from "@/types/admin-portal/appointment-draft";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

export type StatusReason = ReasonOption;

const PREFERENCES_STORAGE_KEY = "admin-portal-appointment-drafts-preferences";

export interface AppointmentDraftsProps {
	drafts?: {
		data: AppointmentDraft[];
		metadata: Metadata;
	};
	filterableCities?: string[];
	admins: Admin[];
	statusReasons: StatusReason[];
	// Filterable row-badge keys, e.g. "self-booked" — see AppointmentDraft::TAG_FILTERS.
	draftTags: string[];
	cancelReasons: StatusReason[];
	mitraAssignmentReasons: StatusReason[];
	// Governs the role badges and the Ubah PIC dialog — see issue #131. The endpoint
	// refuses the write on its own while the flag is off.
	draftPicKpiEnabled?: boolean;
}

export interface AppointmentDraftsGlobalPageProps
	extends BaseGlobalPageProps,
		AppointmentDraftsProps {
	[key: string]: any;
}

// The CANCEL status reason was removed from the selectable list but is still
// stored on cancelled drafts, so keep it available for label resolution.
const CANCEL_STATUS_REASON: StatusReason = {
	labelEn: "Cancel",
	labelId: "Batal",
	value: "CANCEL",
};

// Resolve a stored reason value to its localized label, or a fallback when there is
// no value. Adds CANCEL to the catalogue, which findReasonLabel knows nothing about.
const resolveReasonLabel = (
	reasons: StatusReason[],
	value: string | null | undefined,
	lng: string | undefined,
	fallback: string,
): string =>
	findReasonLabel([...(reasons || []), CANCEL_STATUS_REASON], value, lng) ??
	fallback;

// A Draft shows one reason, the order's: its first Visit holding one, the same pick as
// MitraAssignmentReasonService.order_reason. The helper sends reasoned Visits in order.
const mitraReasonLabel = (
	draft: AppointmentDraft,
	reasons: StatusReason[],
	lng: string,
) => findReasonLabel(reasons, draft.mitraAssignmentReasons?.[0]?.reason, lng);

const columns = (
	handleContinueDraft: (draftId: string) => void,
	handleDeleteDraft: (draftId: string) => void,
	handleUpdateStatusReason: (
		draftId: string,
		currentStatusReason: string | null,
	) => void,
	handleCancelDraft: (draftId: string) => void,
	// Null while DRAFT_PIC_KPI is off, which is also what keeps the menu item away — the
	// endpoint refuses the write in that state anyway.
	handleEditPics: ((draft: AppointmentDraft) => void) | null,
	handleChangeMitraReason: (draft: AppointmentDraft) => void,
	pageURL: string,
	statusReasons: StatusReason[],
	// Empty while MITRA_ASSIGNMENT_REASON is off, which also drops the column.
	mitraReasons: StatusReason[],
	ordersUrl: string,
	t: TFunction,
	lng: string,
): ColumnDef<AppointmentDraft>[] => [
	{
		id: "select",
		header: ({ table }) => {
			// const isChecked = table.getIsAllPageRowsSelected() ||
			//   (table.getIsSomePageRowsSelected() && "indeterminate")

			return (
				<div className="flex items-start space-x-2">
					{/* <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                    className="translate-y-[2px]"
                  />
                </TooltipTrigger>
                <TooltipContent side='top'>
                  <span>{isChecked ? 'Un-select all' : 'Select all'}</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider> */}

					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="border shadow size-8 lg:size-5 border-primary/25"
									onClick={() => table.toggleAllRowsExpanded()}
								>
									{table.getIsAllRowsExpanded() ? (
										<ChevronUpIcon />
									) : (
										<ChevronDownIcon />
									)}
								</Button>
							</TooltipTrigger>
							<TooltipContent side="top">
								<span>
									{table.getIsAllRowsExpanded()
										? t("drafts.expand.collapse_all")
										: t("drafts.expand.expand_all")}
								</span>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
			);
		},
		cell: ({ row }) => {
			const toggleExpand = () => {
				row.toggleExpanded();

				const { queryParams: currentQuery } = populateQueryParams(pageURL);
				const expandedList = removeWhiteSpaces(currentQuery?.expanded || "")
					.split(",")
					.filter(Boolean);
				const id = String(row.original.id);
				const updatedList = row.getIsExpanded()
					? expandedList.filter((item) => item !== id)
					: [...expandedList, id];

				const { fullUrl, queryParams } = populateQueryParams(pageURL, {
					expanded: updatedList.join(","),
				});

				router.get(fullUrl, queryParams, {
					only: ["flash"],
					preserveScroll: true,
					preserveState: true,
					replace: true,
				});
			};

			return (
				<div className="flex items-start space-x-2">
					{/* <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Checkbox
                  checked={row.getIsSelected()}
                  onCheckedChange={(value) => row.toggleSelected(!!value)}
                  aria-label="Select row"
                  className="translate-y-[2px]"
                />
              </TooltipTrigger>
              <TooltipContent side='top'>
                <span>{row.getIsSelected() ? 'Un-select' : 'Select'}</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider> */}

					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="border shadow size-8 lg:size-5 border-primary/25"
									onClick={toggleExpand}
								>
									{row.getIsExpanded() ? (
										<ChevronUpIcon />
									) : (
										<ChevronDownIcon />
									)}
								</Button>
							</TooltipTrigger>
							<TooltipContent side="top">
								<span>
									{row.getIsExpanded()
										? t("drafts.expand.collapse")
										: t("drafts.expand.expand")}
								</span>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
			);
		},
		enableSorting: false,
		enableHiding: false,
	},
	{
		accessorKey: "id",
		header: t("drafts.columns.draft_id"),
		cell: ({ row }) => (
			<span className="font-medium">#{String(row.original.id)}</span>
		),
	},
	{
		id: "requestCode",
		header: t("drafts.columns.request_code"),
		cell: ({ row }) => {
			const requestCode = row.original.formData?.formOptions?.queueCode;

			return (
				<span className="text-sm font-mono font-bold text-nowrap">
					{requestCode || "N/A"}
				</span>
			);
		},
	},
	{
		id: "patientName",
		header: t("drafts.columns.patient"),
		cell: ({ row }) => {
			const patientName = row.original.formData?.patientDetails?.fullName;
			const city = row.original.formData?.patientDetails?.location?.city;

			return (
				<div>
					{/* Both lines stay on one line: a table squeezes a column to its
					    min-content width, which for wrapping text is its longest word — so a
					    patient name would break mid-name and the region under it too. */}
					<p className="font-medium text-sm text-nowrap">
						{patientName?.toUpperCase() || "N/A"}
					</p>
					<p className="text-sm text-muted-foreground/75 block text-nowrap">
						{city || "N/A"}
					</p>
				</div>
			);
		},
	},
	{
		accessorKey: "lastActivity",
		header: t("drafts.columns.last_activity"),
		cell: ({ row }) => {
			const stepLabel =
				DRAFT_STEPS_LABELS[
					row.original.currentStep as keyof typeof DRAFT_STEPS_LABELS
				] || row.original.currentStep;

			return (
				<div className="space-y-1">
					<div className="flex items-center gap-1 text-sm text-muted-foreground text-nowrap">
						<ClockIcon className="size-3 shrink-0" />
						{formatDistanceToNow(new Date(row.original.updatedAt), {
							addSuffix: true,
							locale: id,
						})}
					</div>
					<p className="text-sm text-muted-foreground/75 text-nowrap">
						{t("drafts.step_prefix", { step: stepLabel })}
					</p>
				</div>
			);
		},
	},
	{
		accessorKey: "adminPic",
		header: t("drafts.columns.pics"),
		cell: ({ row }) => <PicDisplay admins={row.original.admins} />,
	},
	{
		accessorKey: "status",
		header: t("drafts.columns.status"),
		cell: ({ row }) => {
			const status = row.original.status;
			const variant =
				status === "expired"
					? "secondary"
					: status === "cancelled"
						? "destructive"
						: status === "scheduled"
							? "success"
							: "default";
			const label =
				status === "expired"
					? t("drafts.status.expired")
					: status === "cancelled"
						? t("drafts.status.cancelled")
						: status === "scheduled"
							? t("drafts.status.scheduled")
							: t("drafts.status.active");
			return (
				<Badge
					variant={variant}
					className="text-[10px] uppercase !px-2 rounded-full"
				>
					{label}
				</Badge>
			);
		},
	},
	{
		id: "statusReason",
		header: t("drafts.columns.status_reason"),
		cell: ({ row }) => {
			const displayReason = resolveReasonLabel(
				statusReasons,
				row.original.statusReason,
				lng,
				t("drafts.na"),
			);
			return (
				<span className="text-sm text-muted-foreground">{displayReason}</span>
			);
		},
	},
	...(mitraReasons.length
		? [
				{
					id: "mitraReason",
					header: t("drafts.columns.mitra_reason"),
					cell: ({ row }: { row: Row<AppointmentDraft> }) => {
						const label = mitraReasonLabel(row.original, mitraReasons, lng);
						return (
							<span className="block min-w-48 max-w-64 text-sm text-pretty">
								{label ?? (
									<span className="text-muted-foreground">
										{t("drafts.na")}
									</span>
								)}
							</span>
						);
					},
				},
			]
		: []),
	{
		id: "regNumber",
		header: t("drafts.columns.reg_number"),
		cell: ({ row }) => {
			const regNumber = row.original.appointmentRegistrationNumber;
			if (!regNumber) {
				return <span className="text-sm text-muted-foreground">N/A</span>;
			}

			return (
				<Link
					href={`${ordersUrl}?search=${encodeURIComponent(regNumber)}`}
					className="text-sm font-medium text-primary text-nowrap hover:underline"
				>
					{regNumber}
				</Link>
			);
		},
	},
	{
		id: "tags",
		header: t("drafts.columns.tags"),
		cell: ({ row }) => <DraftTags draft={row.original} />,
	},
	{
		id: "actions",
		header: "",
		cell: ({ row }) => (
			<div className="flex justify-end gap-2">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" size="icon">
							<MoreHorizontal className="size-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuLabel>{t("drafts.actions.label")}</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							<DropdownMenuItem
								onClick={() => handleContinueDraft(row.original.id)}
								disabled={
									row.original.status === "expired" ||
									row.original.status === "scheduled" ||
									row.original.status === "cancelled"
								}
							>
								<CheckIcon className="size-4" />
								{t("drafts.actions.continue")}
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={() =>
									handleUpdateStatusReason(
										row.original.id,
										row.original.statusReason || null,
									)
								}
							>
								<PencilIcon className="size-4" />
								{t("drafts.actions.update_status_reason")}
							</DropdownMenuItem>
							{handleEditPics && (
								// The quick pickup button stays as it is; this is the other half —
								// assigning a colleague, which today means walking the whole
								// six-step booking form and saving it.
								<DropdownMenuItem
									disabled={
										row.original.status === "cancelled" ||
										row.original.status === "scheduled"
									}
									onClick={() => handleEditPics(row.original)}
								>
									<UsersIcon className="size-4" />
									{t("drafts.actions.edit_pics", { defaultValue: "Ubah PIC" })}
								</DropdownMenuItem>
							)}
							{!!row.original.mitraReasonVisits?.length &&
								!(
									row.original.status === "cancelled" && !row.original.orderId
								) && (
									<DropdownMenuItem
										onClick={() => handleChangeMitraReason(row.original)}
									>
										<UserPenIcon className="size-4" />
										{t("drafts.actions.change_mitra_reason")}
									</DropdownMenuItem>
								)}
							<ChatCopyMenu
								endpoint={`/api/v1/appointments/drafts/${row.original.id}/chat-copy`}
								templates={DRAFT_CHAT_COPY_TEMPLATES}
								label={t("drafts.actions.chat_copy")}
							/>
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							<DropdownMenuItem
								disabled={
									row.original.status === "cancelled" ||
									row.original.status === "scheduled" ||
									row.original.status === "expired"
								}
								onClick={() => handleCancelDraft(row.original.id)}
								className="text-destructive"
							>
								<Ban className="size-4" />
								{t("drafts.actions.cancel")}
							</DropdownMenuItem>
							<DropdownMenuItem
								disabled={row.original.status === "scheduled"}
								onClick={() => handleDeleteDraft(row.original.id)}
								className="text-destructive"
							>
								<Trash2 className="size-4" />
								{t("drafts.actions.delete")}
							</DropdownMenuItem>
						</DropdownMenuGroup>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		),
	},
];

/**
 * The two facts an admin needs before deciding whether to open a Draft: who filled it in,
 * and whether a Mitra is on it. Both change what continuing the Draft involves — a
 * self-booked one carries data nobody confirmed with the patient (ADR 0002), and a Mitra
 * Visit brings the assignment-reason rule with it — so they sit in the row rather than
 * behind the expand toggle.
 */
function DraftTags({
	draft,
	className = "",
}: {
	draft: AppointmentDraft;
	className?: string;
}) {
	const { t } = useTranslation("appointments");

	const tags: RowTag[] = [];
	if (draft.fromCustomer) {
		tags.push({
			key: "self-booked",
			label: t("drafts.badges.self_booked"),
			hint: t("drafts.badges.self_booked_hint"),
			variant: "outline",
		});
	}
	if (draft.mitraVisit) {
		tags.push({
			key: "mitra-visit",
			label: t("drafts.badges.mitra_visit"),
			hint: t("drafts.badges.mitra_visit_hint"),
			variant: "accent",
		});
	}
	// No badge for the without-voucher tag: that one exists so the filter facet can be
	// split in two, and a badge saying what a Draft does not have is noise on every row.
	if (text(draft.formData?.additionalSettings?.voucherCode)) {
		tags.push({
			key: "with-voucher",
			label: t("drafts.badges.with_voucher"),
			hint: t("drafts.badges.with_voucher_hint"),
			variant: "outline",
		});
	}

	return <RowTags tags={tags} className={className} />;
}

interface AdminWithPrimary {
	id: string;
	name: string;
	email: string | null;
	isPrimary: boolean;
	roles?: string[];
}

interface PicDisplayProps {
	admins?: AdminWithPrimary[];
	className?: string;
	badgeClass?: string;
}

function PicDisplay({
	admins,
	className = "",
	badgeClass = "",
}: PicDisplayProps) {
	const { t } = useTranslation("appointments");

	// With DRAFT_PIC_KPI on the server sends a role per PIC, and the badge says which
	// stage each of them owns — "L1/L3" for someone who made the Draft and closed it
	// themselves. One badge per person either way; the list is already deduped.
	//
	// Ordered by slot rather than by pivot order, so the row reads in the order the work
	// happens. Everything the server did not label falls to the end as "Other".
	const withRoles = admins?.filter((admin) => admin.roles?.length);
	if (withRoles?.length) {
		return (
			<div className={`flex items-center gap-1 flex-wrap ${className}`}>
				{[...withRoles]
					.sort((a, b) => picRoleRank(a.roles) - picRoleRank(b.roles))
					.map((admin) => (
						<Badge
							key={admin.id}
							variant="accent"
							className={cn("text-xs", badgeClass)}
						>
							<span className="uppercase">{admin.name}</span>
							<span className="mx-1 font-normal">|</span>
							<span className="font-normal">{admin.roles?.join("/")}</span>
						</Badge>
					))}
			</div>
		);
	}

	const { primaryAdmin, otherAdmins } = admins?.reduce(
		(acc, admin) => {
			if (admin.isPrimary) {
				acc.primaryAdmin = admin;
			} else {
				acc.otherAdmins.push(admin);
			}
			return acc;
		},
		{
			primaryAdmin: null as AdminWithPrimary | null,
			otherAdmins: [] as AdminWithPrimary[],
		},
	) || { primaryAdmin: null, otherAdmins: [] };

	if (!primaryAdmin && !otherAdmins.length) {
		return (
			<span className={`text-sm text-muted-foreground ${className}`}>
				{t("drafts.na")}
			</span>
		);
	}

	return (
		<div className={`flex items-center gap-1 flex-wrap ${className}`}>
			{primaryAdmin && (
				<Badge variant="accent" className={cn("text-xs", badgeClass)}>
					<span className="uppercase">{primaryAdmin.name}</span>
					<span className="mx-1 font-normal">&mdash;</span>
					<span className="font-normal">{t("drafts.primary")}</span>
				</Badge>
			)}

			{otherAdmins.map((admin) => (
				<div key={admin.id}>
					<Badge variant="accent" className={cn("text-xs", badgeClass)}>
						<span className="uppercase">{admin.name}</span>
					</Badge>
				</div>
			))}
		</div>
	);
}

/**
 * A formData value as displayable text, or "" when it is anything else.
 *
 * formData is a schemaless JSONB column with two producers, so a field that should
 * hold a string may not. Optional chaining does not help here: `name?.toUpperCase()`
 * on a number is a TypeError, not undefined, and an object handed to React as a child
 * throws outright — either one takes down the whole Drafts page over one bad row.
 */
const text = (value: unknown) =>
	typeof value === "string"
		? value.trim()
		: typeof value === "number"
			? String(value)
			: "";

/**
 * The Draft's Visits as a horizontal timeline: a solid dot for a Visit that has a
 * date, a hollow one for a Visit still unplanned, so the shape carries the status
 * without the admin having to read every step.
 *
 * Steps wrap onto further rows rather than scrolling sideways: packages run up to 30
 * Visits (Paket Titanium), and a single row of those is roughly ten screen-widths of
 * horizontal scroll to reach the last one.
 *
 * The connector is drawn per step — a half-width rule either side of each dot, blanked
 * only at the very first and last — rather than as one line across the row, which
 * cannot know where the wrap falls. A rule therefore dangles at each row's edge, which
 * reads as the continuation it is.
 */
const VisitTimeline = ({
	visits,
	t,
}: {
	visits: DraftVisit[];
	t: TFunction<"appointments">;
}) => {
	// Same rule as the Orders list: Indonesian is always a 24-hour clock, English follows
	// the admin's own 12/24 preference. Times are read in the clinic's timezone (tzDate),
	// not the browser's, which is what the rest of the appointment pages do.
	const { locale, tzDate, timeFormatDateFns } = useDateContext();
	const timeFormat =
		locale.code === "id" ? DEFAULT_TIME_FORMAT_24_DATE_FNS : timeFormatDateFns;

	return (
		// auto-fill rather than flex-wrap so the columns stay aligned across rows — a
		// wrapped flex row stretches whatever is left over on the last one.
		<ol className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-y-4">
			{visits.map((visit, index) => (
				<li key={visit.visitNumber} className="flex flex-col gap-2">
					<div className="flex items-center" aria-hidden>
						<span
							className={cn(
								"h-px flex-1",
								index === 0 ? "bg-transparent" : "bg-border",
							)}
						/>
						<span
							className={cn(
								"size-3 shrink-0 rounded-full border-2",
								visit.date
									? "border-primary bg-primary"
									: "border-muted-foreground/40 bg-background",
							)}
						/>
						<span
							className={cn(
								"h-px flex-1",
								index === visits.length - 1 ? "bg-transparent" : "bg-border",
							)}
						/>
					</div>
					<div className="px-2 text-center">
						<p className="font-semibold">
							{t("drafts.detail.visit_number", { number: visit.visitNumber })}
						</p>
						{visit.date ? (
							<>
								<p className="text-xs text-muted-foreground">
									{format(visit.date, "EEE, dd MMM yyyy", {
										locale,
										in: tzDate,
									})}
								</p>
								<p className="text-xs text-muted-foreground">
									{format(visit.date, timeFormat, { locale, in: tzDate })}
									{" · "}
									{visit.therapistName || t("drafts.detail.visit_unassigned")}
								</p>
							</>
						) : (
							<p className="text-xs italic text-muted-foreground/70">
								{t("drafts.detail.visit_unscheduled")}
							</p>
						)}
					</div>
				</li>
			))}
		</ol>
	);
};

interface DraftDetailsProps {
	row: Row<AppointmentDraft>;
	handleContinueDraft: (draftId: string) => void;
	handleDeleteDraft: (draftId: string) => void;
	handleUpdateStatusReason: (
		draftId: string,
		currentStatusReason: string | null,
	) => void;
	statusReasons: StatusReason[];
	cancelReasons: StatusReason[];
	mitraAssignmentReasons: StatusReason[];
}

const DraftDetails = ({
	row,
	handleContinueDraft,
	handleDeleteDraft,
	handleUpdateStatusReason,
	statusReasons,
	cancelReasons,
	mitraAssignmentReasons,
}: DraftDetailsProps) => {
	const { t, i18n } = useTranslation("appointments");
	const lng = i18n.language;
	const draft = row.original;

	// Patient data from formData
	const patientData = draft.formData?.patientDetails;
	const patientContact = draft.formData?.contactInformation;
	// Resolved client-side so the label follows the admin's locale; the serializer's
	// mitraAssignmentReasonLabel is Indonesian-only and exists for other consumers.
	// Null for a KARPIS-only Draft, which is what hides the section entirely.
	// Booking data lives in formData, names and all — the scheduling step stores
	// service/package as {id, name}, so nothing here needs resolving against props.
	const scheduling = draft.formData?.appointmentScheduling;
	const visits = draftVisits(draft.formData);
	// The dates the patient asked for. Empty for every Draft an admin filled in here —
	// only the WhatsApp/customer path collects them — which is what hides the block.
	const candidates = preferredSchedules(scheduling);
	const bookingBlocks = scheduling
		? [
				{
					id: "service",
					// Service names are stored as constants (FISIOHOME_SPECIAL_TIER), which
					// is not what an admin should have to read.
					label: t("drafts.detail.service"),
					value:
						text(scheduling.service?.name).replaceAll("_", " ") ||
						t("drafts.na"),
				},
				{
					id: "package",
					label: t("drafts.detail.package"),
					value:
						[
							text(scheduling.package?.name).toUpperCase(),
							Number(scheduling.package?.numberOfVisit) > 0
								? t("drafts.detail.visit_count", {
										count: Number(scheduling.package.numberOfVisit),
									})
								: "",
						]
							.filter(Boolean)
							.join(" • ") || t("drafts.na"),
				},
				{
					id: "preferred_therapist_gender",
					label: t("drafts.detail.preferred_therapist_gender"),
					value: text(scheduling.preferredTherapistGender) || t("drafts.na"),
				},
				{
					id: "voucher_code",
					label: t("drafts.detail.voucher_code"),
					value:
						text(draft.formData?.additionalSettings?.voucherCode) ||
						t("drafts.detail.no_voucher"),
				},
			]
		: [];
	const mitraAssignmentReasonLabel = mitraReasonLabel(
		draft,
		mitraAssignmentReasons,
		lng,
	);

	const infoBlocks = [
		{
			id: "pics",
			label: t("drafts.columns.pics"),
			value: <PicDisplay admins={draft.admins} badgeClass="bg-card shadow" />,
		},
		{
			id: "last_step",
			label: t("drafts.detail.last_step"),
			value:
				DRAFT_STEPS_LABELS[
					draft.currentStep as keyof typeof DRAFT_STEPS_LABELS
				] || draft.currentStep,
		},
		{
			id: "last_updated",
			label: t("drafts.detail.last_updated"),
			value: formatDistanceToNow(new Date(draft.updatedAt), {
				addSuffix: true,
				locale: id,
			}),
		},
		{
			id: "created_at",
			label: t("drafts.detail.created_at"),
			value: format(new Date(draft.createdAt), "dd MMM yyyy, HH:mm", {
				locale: id,
			}),
		},
		// Only on the detail panel, never as a column: the table is already dense, and the
		// lead time this feeds belongs on a dashboard rather than on the daily work screen.
		// Absent unless the Draft became an Order, and null while DRAFT_PIC_KPI is off.
		...(draft.submittedAt
			? [
					{
						id: "submitted_at",
						label: t("drafts.detail.submitted_at", {
							defaultValue: "Jadi order pada",
						}),
						value: format(new Date(draft.submittedAt), "dd MMM yyyy, HH:mm", {
							locale: id,
						}),
					},
				]
			: []),
		{
			id: "expires_at",
			label: t("drafts.detail.expires_at"),
			// A customer-created Draft has no expiry until an admin picks it up. Guarded
			// because new Date(null) is 1 Jan 1970 rather than an error, so the unguarded
			// version silently rendered a Draft as decades overdue.
			value: draft.expiresAt
				? format(new Date(draft.expiresAt), "dd MMM yyyy, HH:mm", {
						locale: id,
					})
				: t("drafts.na"),
		},
		{
			id: "status_reason",
			label: t("drafts.columns.status_reason"),
			value: resolveReasonLabel(
				statusReasons,
				draft.statusReason,
				lng,
				t("drafts.na"),
			),
		},
	];

	// Add patient info blocks if patient data exists
	if (patientData) {
		const patientInfoBlocks = [
			{
				id: "name",
				label: t("drafts.detail.name"),
				value: patientData.fullName?.toUpperCase() || "N/A",
				isPatient: true,
			},
			{
				id: "age",
				label: t("drafts.detail.age"),
				value:
					patientData.age && patientData.dateOfBirth
						? t("drafts.detail.age_with_dob", {
								age: patientData.age,
								dob: format(new Date(patientData.dateOfBirth), "dd MMM yyyy", {
									locale: id,
								}),
							})
						: patientData.age
							? t("drafts.detail.age_value", { age: patientData.age })
							: "N/A",
				isPatient: true,
			},
			{
				id: "gender",
				label: t("drafts.detail.gender"),
				value: patientData.gender ? patientData.gender.toUpperCase() : "N/A",
				isPatient: true,
			},
			{
				id: "patient_contact",
				label: t("drafts.detail.patient_contact"),
				value: patientContact?.contactName?.toUpperCase() || "N/A",
				isPatient: true,
			},
			{
				id: "phone",
				label: t("drafts.detail.phone"),
				value: patientContact?.contactPhone || "N/A",
				isPatient: true,
			},
			{
				id: "email",
				label: t("drafts.detail.email"),
				value: patientContact?.email || "N/A",
				isPatient: true,
			},
			{
				id: "location",
				label: t("drafts.detail.location"),
				value: patientData?.location?.city || "N/A",
				isPatient: true,
			},
		];

		// Insert patient info blocks after the basic info blocks
		infoBlocks.splice(5, 0, ...patientInfoBlocks);
	}

	return (
		<div className="p-4 space-y-4 rounded-xl border bg-card text-card-foreground md:p-6 text-sm">
			<div className="space-y-1">
				<h4 className="text-base font-bold tracking-tight">
					{t("drafts.detail.title", { id: draft.id })}
				</h4>
			</div>

			<div className="space-y-4">
				{/* Draft Information Section */}
				<div className="space-y-2">
					<div className="flex items-center gap-2">
						<NotepadTextDashedIcon className="size-4 text-muted-foreground" />
						<h5 className="text-sm font-semibold uppercase text-muted-foreground">
							{t("drafts.detail.draft_information")}
						</h5>
					</div>
					<div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
						{infoBlocks
							.filter((item) => !(item as any).isPatient)
							.map((item) => (
								<div
									key={item.id}
									className={cn(
										"space-y-1 rounded-lg bg-background p-3",
										item.id === "pics" ? "col-span-full" : "col-span-1",
									)}
								>
									<p className="text-xs uppercase text-muted-foreground">
										{item.label}
									</p>
									{item.id === "last_step" ? (
										<p className="font-semibold text-pretty mb-1">
											{item.value}
										</p>
									) : typeof item.value === "string" ? (
										<p className="font-semibold text-pretty">{item.value}</p>
									) : (
										item.value
									)}
								</div>
							))}
					</div>
				</div>

				{/* Patient Information Section */}
				{patientData && (
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<User className="size-4 text-muted-foreground" />
							<h5 className="text-sm font-semibold uppercase text-muted-foreground">
								{t("drafts.detail.patient_information")}
							</h5>
						</div>
						<div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
							{infoBlocks
								.filter((item) => (item as any).isPatient)
								.map((item) => (
									<div
										key={item.id}
										className="space-y-1 rounded-lg bg-background p-3"
									>
										<p className="text-xs uppercase text-muted-foreground">
											{item.label}
										</p>
										{typeof item.value === "string" ? (
											<p className="font-semibold text-pretty">{item.value}</p>
										) : (
											item.value
										)}
									</div>
								))}
						</div>
					</div>
				)}

				{/* Booking Section — hidden entirely for a Draft abandoned before the
				    scheduling step, which has no service or package to show. */}
				{!!bookingBlocks.length && (
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<Package className="size-4 text-muted-foreground" />
							<h5 className="text-sm font-semibold uppercase text-muted-foreground">
								{t("drafts.detail.booking_information")}
							</h5>
						</div>
						<div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
							{bookingBlocks.map((item) => (
								<div
									key={item.id}
									className="space-y-1 rounded-lg bg-background p-3"
								>
									<p className="text-xs uppercase text-muted-foreground">
										{item.label}
									</p>
									<p className="font-semibold text-pretty">{item.value}</p>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Visits Section */}
				{!!visits.length && (
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<CalendarClock className="size-4 text-muted-foreground" />
							<h5 className="text-sm font-semibold uppercase text-muted-foreground">
								{t("drafts.detail.visits")}
							</h5>
						</div>
						<div className="space-y-3 rounded-lg bg-background p-3">
							<PreferredSchedules schedules={candidates} />
							<VisitTimeline visits={visits} t={t} />
						</div>
					</div>
				)}

				{/* Mitra Assignment Section — only for a Booking that has a Mitra Visit.
				    A KARPIS-only Draft shows nothing rather than an empty label: the
				    question was never put to the admin who filled it in. */}
				{mitraAssignmentReasonLabel && (
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<Handshake className="size-4 text-muted-foreground" />
							<h5 className="text-sm font-semibold uppercase text-muted-foreground">
								{t("drafts.detail.mitra_assignment")}
							</h5>
						</div>
						<div className="space-y-1 rounded-lg bg-background p-3">
							<p className="text-xs uppercase text-muted-foreground">
								{t("drafts.detail.mitra_assignment_reason")}
							</p>
							<p className="font-semibold text-pretty">
								{mitraAssignmentReasonLabel}
							</p>
						</div>
					</div>
				)}

				{/* Cancellation Section */}
				{draft.status === "cancelled" && (
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<Ban className="size-4 text-destructive" />
							<h5 className="text-sm font-semibold uppercase text-destructive">
								{t("drafts.detail.cancellation")}
							</h5>
						</div>
						<div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
							<div className="col-span-full space-y-1 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
								<p className="text-xs uppercase text-muted-foreground">
									{t("drafts.detail.cancel_reason")}
								</p>
								<p className="font-semibold text-pretty">
									{resolveReasonLabel(
										cancelReasons,
										draft.cancelReason,
										lng,
										t("drafts.na"),
									)}
								</p>
							</div>
							<div className="col-span-1 space-y-1 rounded-lg bg-background p-3">
								<p className="text-xs uppercase text-muted-foreground">
									{t("drafts.detail.cancelled_by")}
								</p>
								<p className="font-semibold text-pretty">
									{draft.cancelledBy?.name || t("drafts.na")}
								</p>
							</div>
							<div className="col-span-1 space-y-1 rounded-lg bg-background p-3">
								<p className="text-xs uppercase text-muted-foreground">
									{t("drafts.detail.cancelled_at")}
								</p>
								<p className="font-semibold text-pretty">
									{draft.cancelledAt
										? format(
												new Date(draft.cancelledAt),
												"dd MMM yyyy, HH:mm",
												{
													locale: id,
												},
											)
										: "N/A"}
								</p>
							</div>
						</div>
					</div>
				)}
			</div>

			<div className="flex justify-end gap-2">
				<Button
					variant="ghost-primary"
					onClick={() => handleContinueDraft(row.original.id)}
					disabled={
						draft.status === "expired" ||
						draft.status === "scheduled" ||
						draft.status === "cancelled"
					}
				>
					<CheckIcon className="size-4" />
					{t("drafts.actions.continue")}
				</Button>
				<Button
					variant="ghost"
					onClick={() =>
						handleUpdateStatusReason(
							row.original.id,
							row.original.statusReason || null,
						)
					}
				>
					<PencilIcon className="size-4" />
					{t("drafts.actions.update_status_reason")}
				</Button>
				<Button
					variant="ghost-destructive"
					onClick={() => handleDeleteDraft(row.original.id)}
				>
					<Trash2 className="size-4" />
					{t("drafts.detail.delete")}
				</Button>
				<Button
					variant="ghost"
					onClick={() => row.getToggleExpandedHandler()()}
				>
					{t("drafts.detail.close")}
				</Button>
			</div>
		</div>
	);
};

// Type for delete dialog state
interface DeleteDialogState {
	isOpen: boolean;
	title: string;
	description: string;
	onOpenChange: (open: boolean) => void;
}

const AppointmentDrafts = () => {
	const { t, i18n } = useTranslation("appointments");
	const lng = i18n.language;
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentDraftsGlobalPageProps>();
	const currentUser = globalProps.auth?.currentUser;

	const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
		isOpen: false,
		title: "",
		description: "",
		onOpenChange: () => {},
	});
	const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
	const [mitraReasonTarget, setMitraReasonTarget] =
		useState<MitraAssignmentReasonTarget | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [picConfirmDialog, setPicConfirmDialog] = useState<{
		isOpen: boolean;
		draftId: string | null;
		// What the admin asked to be. "supporting" is the default and the only option
		// while DRAFT_PIC_KPI is off; L1 is never offered — it is claimed automatically
		// when nobody holds it, and taken from nobody when somebody does.
		slot: "supporting" | "l2" | "l3";
	}>({
		isOpen: false,
		draftId: null,
		slot: "supporting",
	});
	const [statusReasonDialog, setStatusReasonDialog] = useState<{
		isOpen: boolean;
		draftId: string | null;
		currentStatusReason: string | null;
	}>({
		isOpen: false,
		draftId: null,
		currentStatusReason: null,
	});
	const [cancelDialog, setCancelDialog] = useState<{
		isOpen: boolean;
		draftId: string | null;
		reason: string;
	}>({
		isOpen: false,
		draftId: null,
		reason: "",
	});
	const [picSlotsDialog, setPicSlotsDialog] = useState<{
		isOpen: boolean;
		draftId: string | null;
		slots: PicSlots;
		// The supporting PICs, as ids. Edited here too, so assigning a colleague never
		// means walking the six-step booking form — which is the whole point of this
		// dialog. Slot holders are not in it: they are on the Draft through their slot.
		supporting: string[];
		saving: boolean;
	}>({
		isOpen: false,
		draftId: null,
		slots: EMPTY_PIC_SLOTS,
		supporting: [],
		saving: false,
	});

	const draftsData = globalProps?.drafts?.data || [];
	// The Draft the pickup dialog is about, so it can grey out the slots already held.
	const picConfirmDraft = draftsData.find(
		(draft) => draft.id === picConfirmDialog.draftId,
	);
	const draftsMetadata = globalProps?.drafts?.metadata || null;

	// The filter panel owns the filter state; the page reads the URL, which is the shared
	// source of truth between them. Only the values the page itself still needs are
	// derived here — the expired-status alert and the empty-state copy.
	const currentQuery = globalProps?.adminPortal?.currentQuery;
	const currentAdminId = currentQuery?.adminId || "me";
	const currentStatus = currentQuery?.status || "all";
	const selectedAdminName = (adminId: string) =>
		globalProps.admins?.find((admin) => String(admin.id) === adminId)?.name;

	// Drives the count badge on the collapsed Filters button. Mirrors the panel's own
	// rule: status and ownership only count once they differ from the page defaults.
	const activeFilterCount = useMemo(
		() =>
			[
				currentQuery?.draftId,
				currentQuery?.patient,
				currentQuery?.city,
				currentQuery?.statusReason,
				currentStatus !== "all" ? currentStatus : "",
				currentAdminId !== "me" ? currentAdminId : "",
				// One count per range, not per endpoint — the panel shows one control and
				// one pill for each, so counting both ends would inflate the badge.
				currentQuery?.createdFrom || currentQuery?.createdTo,
				currentQuery?.scheduledFrom || currentQuery?.scheduledTo,
			].filter(Boolean).length,
		[currentQuery, currentStatus, currentAdminId],
	);

	const isMobile = useIsMobile();
	const [preferences, savePreferences] = useLocalStorage(
		PREFERENCES_STORAGE_KEY,
		{ isShowFilter: false },
	);
	const handleToggleFilter = useCallback(() => {
		savePreferences((prev) => ({ ...prev, isShowFilter: !prev.isShowFilter }));
	}, [savePreferences]);

	const updatePageParam = useCallback(
		(params: { page?: number; limit?: number }) => {
			const { fullUrl } = populateQueryParams(pageURL, params);
			router.get(
				fullUrl,
				{},
				{
					preserveScroll: true,
					preserveState: true,
					only: ["adminPortal", "flash", "errors", "drafts"],
				},
			);
		},
		[pageURL],
	);

	const handleContinueDraft = useCallback(
		(draftId: string) => {
			const draft = draftsData.find((d) => d.id === draftId);

			if (draft?.status === "expired" || draft?.status === "scheduled") {
				return;
			}
			const isCurrentUserPic = draft?.admins?.some(
				(admin) => String(admin.id) === String(currentUser?.id),
			);

			if (!isCurrentUserPic) {
				// Show confirmation dialog for non-PIC users
				setPicConfirmDialog({
					isOpen: true,
					draftId: draftId,
					slot: "supporting",
				});
			} else {
				// Continue directly for PIC users
				router.get(
					globalProps.adminPortal.router.adminPortal.appointment.new,
					{ draft_id: draftId },
					{ preserveState: false },
				);
			}
		},
		[
			draftsData,
			currentUser?.id,
			globalProps.adminPortal.router.adminPortal.appointment.new,
		],
	);

	const handleDeleteDraft = useCallback(
		(draftId: string) => {
			setDraftToDelete(draftId);
			setDeleteDialog({
				isOpen: true,
				title: t("drafts.delete_dialog.title"),
				description: t("drafts.delete_dialog.description"),
				onOpenChange: (open: boolean) => {
					if (!open) {
						setDraftToDelete(null);
					}
					setDeleteDialog((prev) => ({ ...prev, isOpen: open }));
				},
			});
		},
		[t],
	);

	const handleConfirmPicAddition = useCallback(async () => {
		if (!picConfirmDialog.draftId) return;

		try {
			// Call API to add current user as PIC
			const response = await fetch(
				`/api/v1/appointments/drafts/${picConfirmDialog.draftId}/add_pic`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					// Omitted for a plain supporting PIC, which is what the server assumes
					// when it sees no slot.
					body: JSON.stringify(
						picConfirmDialog.slot === "supporting"
							? {}
							: { slot: picConfirmDialog.slot },
					),
				},
			);

			if (!response.ok) {
				const error = await response.json();
				toast.error(error.error || t("drafts.toasts.add_pic_failed"));
				return;
			}

			// Successfully added as PIC, now redirect to the draft form
			router.get(
				globalProps.adminPortal.router.adminPortal.appointment.new,
				{ draft_id: picConfirmDialog.draftId },
				{ preserveState: false },
			);
			setPicConfirmDialog({ isOpen: false, draftId: null, slot: "supporting" });
		} catch (error) {
			console.error("Add PIC error:", error);
			toast.error(t("drafts.toasts.add_pic_failed"));
		}
	}, [
		picConfirmDialog.draftId,
		picConfirmDialog.slot,
		globalProps.adminPortal.router.adminPortal.appointment.new,
		t,
	]);

	const handleCancelPicAddition = useCallback(() => {
		setPicConfirmDialog({ isOpen: false, draftId: null, slot: "supporting" });
	}, []);

	const handleUpdateStatusReason = useCallback(
		(draftId: string, currentStatusReason: string | null) => {
			setStatusReasonDialog({
				isOpen: true,
				draftId,
				currentStatusReason,
			});
		},
		[],
	);

	const handleConfirmStatusReasonUpdate = useCallback(
		async (newStatusReason: string) => {
			if (!statusReasonDialog.draftId) return;

			try {
				const response = await fetch(
					`/api/v1/appointments/drafts/${statusReasonDialog.draftId}/status_reason`,
					{
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
							Accept: "application/json",
							"X-CSRF-Token": getCsrfToken(),
						},
						body: JSON.stringify({
							status_reason: newStatusReason,
						}),
					},
				);

				if (!response.ok) {
					const error = await response.json();
					toast.error(
						error.error || t("drafts.toasts.status_reason_update_failed"),
					);
					return;
				}

				toast.success(t("drafts.toasts.status_reason_updated"));
				setStatusReasonDialog({
					isOpen: false,
					draftId: null,
					currentStatusReason: null,
				});
				router.reload({
					only: ["adminPortal", "flash", "errors", "drafts"],
				});
			} catch (error) {
				console.error("Update status reason error:", error);
				toast.error(t("drafts.toasts.status_reason_update_failed"));
			}
		},
		[statusReasonDialog.draftId, t],
	);

	// Seeded from the Draft's own columns, which the serializer withholds while the flag
	// is off — so the dialog can only ever open on a Draft whose slots are real.
	const handleEditPics = useCallback((draft: AppointmentDraft) => {
		setPicSlotsDialog({
			isOpen: true,
			draftId: draft.id,
			slots: {
				picL1: draft.createdByAdmin?.id ?? null,
				picL2: draft.continuedByAdmin?.id ?? null,
				picL3: draft.submittedByAdmin?.id ?? null,
			},
			// Everyone the server labelled "Other" — a PIC holding none of the slots. Read
			// off the role rather than recomputed here, so the dialog and the row badge
			// can never disagree about who counts as supporting.
			supporting: (draft.admins ?? [])
				.filter((admin) => admin.roles?.includes(OTHER_PIC_ROLE))
				.map((admin) => String(admin.id)),
			saving: false,
		});
	}, []);

	const handleConfirmPicSlots = useCallback(async () => {
		if (!picSlotsDialog.draftId) return;

		setPicSlotsDialog((prev) => ({ ...prev, saving: true }));
		try {
			const response = await fetch(
				`/api/v1/appointments/drafts/${picSlotsDialog.draftId}/pics`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({
						pic_l1: picSlotsDialog.slots.picL1,
						pic_l2: picSlotsDialog.slots.picL2,
						pic_l3: picSlotsDialog.slots.picL3,
						// Always sent, empty included — a missing key would tell the server
						// to leave the supporting list alone, making it impossible to clear.
						admin_ids: picSlotsDialog.supporting,
					}),
				},
			);

			if (!response.ok) {
				const error = await response.json();
				toast.error(
					error.error ||
						t("drafts.toasts.edit_pics_failed", {
							defaultValue: "Gagal memperbarui PIC draf.",
						}),
				);
				setPicSlotsDialog((prev) => ({ ...prev, saving: false }));
				return;
			}

			toast.success(
				t("drafts.toasts.edit_pics_success", {
					defaultValue: "PIC draf diperbarui.",
				}),
			);
			setPicSlotsDialog({
				isOpen: false,
				draftId: null,
				slots: EMPTY_PIC_SLOTS,
				supporting: [],
				saving: false,
			});
			router.reload({ only: ["adminPortal", "flash", "errors", "drafts"] });
		} catch (error) {
			console.error("Update draft PICs error:", error);
			toast.error(
				t("drafts.toasts.edit_pics_failed", {
					defaultValue: "Gagal memperbarui PIC draf.",
				}),
			);
			setPicSlotsDialog((prev) => ({ ...prev, saving: false }));
		}
	}, [
		picSlotsDialog.draftId,
		picSlotsDialog.slots,
		picSlotsDialog.supporting,
		t,
	]);

	const handleCancelDraft = useCallback((draftId: string) => {
		setCancelDialog({ isOpen: true, draftId, reason: "" });
	}, []);

	const handleConfirmCancelDraft = useCallback(async () => {
		if (!cancelDialog.draftId || !cancelDialog.reason.trim()) return;

		try {
			const response = await fetch(
				`/api/v1/appointments/drafts/${cancelDialog.draftId}/cancel`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({ cancel_reason: cancelDialog.reason.trim() }),
				},
			);

			if (!response.ok) {
				const error = await response.json();
				toast.error(error.error || t("drafts.toasts.cancel_failed"));
				return;
			}

			toast.success(t("drafts.toasts.cancel_success"));
			setCancelDialog({ isOpen: false, draftId: null, reason: "" });
			router.reload({
				only: ["adminPortal", "flash", "errors", "drafts"],
			});
		} catch (error) {
			console.error("Cancel draft error:", error);
			toast.error(t("drafts.toasts.cancel_failed"));
		}
	}, [cancelDialog.draftId, cancelDialog.reason, t]);

	const currentExpanded = useMemo<ExpandedState>(() => {
		const { queryParams } = populateQueryParams(pageURL);
		const expandedList = queryParams?.expanded
			? removeWhiteSpaces(queryParams?.expanded)?.split(",")
			: [];
		const draftsIndex = draftsData.reduce(
			(obj, item, index) => {
				if (expandedList.includes(String(item.id))) {
					obj[index] = true;
				}
				return obj;
			},
			{} as Record<number, boolean>,
		);

		return draftsIndex;
	}, [pageURL, draftsData]);

	const tableColumns = useMemo(
		() =>
			columns(
				handleContinueDraft,
				handleDeleteDraft,
				handleUpdateStatusReason,
				handleCancelDraft,
				globalProps?.draftPicKpiEnabled ? handleEditPics : null,
				(draft) =>
					setMitraReasonTarget({
						url: draft.orderId
							? `/admin-portal/appointments/orders/${draft.orderId}/mitra-assignment-reason`
							: `/admin-portal/appointments/drafts/${draft.id}/mitra-assignment-reason`,
						visits: draft.mitraReasonVisits ?? [],
					}),
				pageURL,
				globalProps?.statusReasons || [],
				globalProps?.mitraAssignmentReasons || [],
				"/admin-portal/appointments/orders",
				t,
				lng,
			),
		[
			handleContinueDraft,
			handleDeleteDraft,
			handleUpdateStatusReason,
			handleCancelDraft,
			handleEditPics,
			globalProps?.draftPicKpiEnabled,
			pageURL,
			globalProps?.statusReasons,
			globalProps?.mitraAssignmentReasons,
			t,
			lng,
		],
	);

	const confirmDeleteDraft = async () => {
		if (!draftToDelete) return;

		setIsDeleting(true);
		try {
			const response = await fetch(
				`/api/v1/appointments/drafts/${draftToDelete}`,
				{
					method: "DELETE",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
				},
			);

			if (response.ok) {
				toast.success(t("drafts.toasts.delete_success"));
				router.reload({
					only: ["adminPortal", "flash", "errors", "drafts"],
					data: {
						admin_id:
							currentAdminId !== "me" && currentAdminId !== "all"
								? currentAdminId
								: null,
						draft_id: currentQuery?.draftId || null,
					},
				});
			} else {
				const error = await response.json();
				toast.error(error.error || t("drafts.toasts.delete_failed"));
			}
		} catch (error) {
			console.error("Delete draft error:", error);
			toast.error(t("drafts.toasts.delete_failed"));
		} finally {
			setIsDeleting(false);
			setDeleteDialog({
				isOpen: false,
				title: "",
				description: "",
				onOpenChange: () => {},
			});
			setDraftToDelete(null);
		}
	};

	return (
		<>
			<Head title={t("drafts.head_title")} />

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-4">
				<div>
					<h1 className="text-lg font-bold tracking-tight uppercase">
						{t("drafts.page_title")}
					</h1>

					<p className="w-full text-sm text-muted-foreground text-pretty md:max-w-[60ch]">
						{t("drafts.page_description")}
					</p>
				</div>

				<Separator className="bg-border" />

				<ApptViewChanger activeView="drafts" />

				<div className="space-y-2">
					<div className="z-10 flex items-center justify-between gap-2">
						<Button
							type="button"
							variant={preferences.isShowFilter ? "default" : "outline"}
							size={isMobile ? "icon" : "default"}
							className="shadow-none"
							onClick={(event) => {
								event.preventDefault();
								handleToggleFilter();
							}}
						>
							<ListFilterIcon className="size-4" />
							{isMobile
								? null
								: preferences.isShowFilter
									? t("button.close_filter")
									: t("button.filter")}
							{!preferences.isShowFilter && activeFilterCount > 0 && (
								<Badge variant="accent" className="px-1 rounded-full">
									{activeFilterCount}
								</Badge>
							)}
						</Button>

						<div className="flex items-center gap-2">
							<PasteWaFormButton />

							<Button
								asChild
								size={isMobile ? "icon" : "default"}
								className="shadow-none"
							>
								<Link
									href={
										globalProps.adminPortal.router.adminPortal.appointment.new
									}
								>
									<PlusIcon />
									{isMobile ? null : t("button.create")}
								</Link>
							</Button>
						</div>
					</div>

					<AnimatePresence initial={false}>
						{preferences.isShowFilter && (
							<motion.section
								initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
								animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
								exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
								transition={{ duration: 0.3, ease: "easeOut" }}
							>
								<DraftFilterList onToggleShow={handleToggleFilter} />
							</motion.section>
						)}
					</AnimatePresence>
				</div>

				<div className="space-y-4">
					{currentStatus === "expired" && (
						<Alert>
							<ClockIcon className="size-4" />
							<AlertTitle>{t("drafts.expired_alert.title")}</AlertTitle>
							<AlertDescription>
								{t("drafts.expired_alert.description")}
							</AlertDescription>
						</Alert>
					)}
					<Deferred
						data={["drafts"]}
						fallback={<Skeleton className="w-full rounded-md h-64" />}
					>
						{draftsData.length === 0 ? (
							<Card>
								<CardContent className="flex flex-col items-center justify-center py-12">
									<NotepadTextDashedIcon className="size-12 text-muted-foreground mb-4 opacity-60" />
									<h3 className="text-lg font-semibold mb-2">
										{t("drafts.empty.title")}
									</h3>
									<p className="text-muted-foreground text-center mb-4">
										{currentAdminId === "me"
											? t("drafts.empty.me")
											: currentAdminId === "all"
												? t("drafts.empty.all")
												: currentAdminId === "unassigned"
													? t("drafts.empty.unassigned")
													: t("drafts.empty.admin", {
															name:
																selectedAdminName(currentAdminId) ||
																t("drafts.empty.selected_admin"),
														})}
									</p>
									<Button asChild>
										<Link
											href={
												globalProps.adminPortal.router.adminPortal.appointment
													.new
											}
										>
											<PlusIcon />
											{t("button.create")}
										</Link>
									</Button>
								</CardContent>
							</Card>
						) : (
							<DataTable
								columns={tableColumns}
								data={draftsData}
								subComponent={(row) => (
									<DraftDetails
										row={row}
										handleContinueDraft={handleContinueDraft}
										handleDeleteDraft={handleDeleteDraft}
										handleUpdateStatusReason={handleUpdateStatusReason}
										statusReasons={globalProps?.statusReasons || []}
										cancelReasons={globalProps?.cancelReasons || []}
										mitraAssignmentReasons={
											globalProps?.mitraAssignmentReasons || []
										}
									/>
								)}
								currentExpanded={currentExpanded}
								// Replaces DataTable's own client-side pager. Rendering ApptPagination
								// alongside the table instead would show both at once, each with its
								// own page size.
								customPagination={() =>
									draftsMetadata ? (
										<ApptPagination
											metadata={draftsMetadata}
											label={t("drafts.filters.pagination_label")}
											actions={{
												goToPrevpage: () =>
													updatePageParam({
														page: Math.max(1, (draftsMetadata?.page || 1) - 1),
													}),
												goToNextPage: () =>
													updatePageParam({
														page: Math.min(
															draftsMetadata?.pages || 1,
															(draftsMetadata?.page || 1) + 1,
														),
													}),
												onChangeLimit: (value) =>
													updatePageParam({ page: 1, limit: Number(value) }),
												goToPage: (value) =>
													updatePageParam({ page: Number(value) }),
												goToFirstPage: () => updatePageParam({ page: 1 }),
												goToLastPage: () =>
													updatePageParam({ page: draftsMetadata?.pages || 1 }),
											}}
										/>
									) : null
								}
							/>
						)}
					</Deferred>
				</div>
			</PageContainer>

			<AlertDialog
				open={deleteDialog.isOpen}
				onOpenChange={(open) => {
					if (!open) {
						setDeleteDialog({
							isOpen: false,
							title: "",
							description: "",
							onOpenChange: () => {},
						});
						setDraftToDelete(null);
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{deleteDialog.title}</AlertDialogTitle>
						<AlertDialogDescription>
							{deleteDialog.description}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>
							{t("drafts.delete_dialog.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmDeleteDraft}
							disabled={isDeleting}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{isDeleting
								? t("drafts.delete_dialog.deleting")
								: t("drafts.delete_dialog.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={picConfirmDialog.isOpen}
				onOpenChange={(open) => {
					if (!open) {
						handleCancelPicAddition();
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("drafts.pic_dialog.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("drafts.pic_dialog.description")}
						</AlertDialogDescription>
					</AlertDialogHeader>

					{/* L1 is deliberately not on this list: it is claimed automatically when
					    nobody holds it, and never taken from whoever does. A slot somebody
					    already holds is not offered either — reassigning one is the Ubah PIC
					    dialog's job, not something a pickup should do on the way past. The
					    server checks both again; this only keeps the choice honest on screen. */}
					{globalProps?.draftPicKpiEnabled && (
						<RadioGroup
							value={picConfirmDialog.slot}
							onValueChange={(value) =>
								setPicConfirmDialog((prev) => ({
									...prev,
									slot: value as "supporting" | "l2" | "l3",
								}))
							}
							className="gap-3 px-0.5 pb-2"
						>
							{[
								{
									value: "supporting" as const,
									label: t("drafts.pic_dialog.as_supporting", {
										defaultValue: "Sebagai PIC pendukung",
									}),
									taken: null as string | null,
								},
								{
									value: "l2" as const,
									label: t("drafts.pic_dialog.as_l2", {
										defaultValue: "Sebagai L2 — yang melanjutkan",
									}),
									taken: picConfirmDraft?.continuedByAdmin?.name ?? null,
								},
								{
									value: "l3" as const,
									label: t("drafts.pic_dialog.as_l3", {
										defaultValue: "Sebagai L3 — yang men-submit jadi order",
									}),
									taken: picConfirmDraft?.submittedByAdmin?.name ?? null,
								},
							].map((option) => (
								<div key={option.value} className="flex items-start gap-2">
									<RadioGroupItem
										value={option.value}
										id={`pic-slot-${option.value}`}
										disabled={!!option.taken}
										className="mt-0.5"
									/>
									<Label
										htmlFor={`pic-slot-${option.value}`}
										className={cn(
											"font-normal leading-snug",
											option.taken && "text-muted-foreground",
										)}
									>
										{option.label}
										{option.taken && (
											<span className="block text-xs">
												{t("drafts.pic_dialog.slot_taken", {
													name: option.taken,
													defaultValue: `Sudah dipegang ${option.taken}`,
												})}
											</span>
										)}
									</Label>
								</div>
							))}
						</RadioGroup>
					)}
					<AlertDialogFooter>
						<AlertDialogCancel onClick={handleCancelPicAddition}>
							{t("drafts.pic_dialog.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmPicAddition}>
							{t("drafts.pic_dialog.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<MitraAssignmentReasonDialog
				target={mitraReasonTarget}
				options={globalProps?.mitraAssignmentReasons || []}
				onClose={() => setMitraReasonTarget(null)}
			/>

			<Dialog
				open={statusReasonDialog.isOpen}
				onOpenChange={(open) => {
					if (!open) {
						setStatusReasonDialog({
							isOpen: false,
							draftId: null,
							currentStatusReason: null,
						});
					}
				}}
			>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle>{t("drafts.status_reason_dialog.title")}</DialogTitle>
						<DialogDescription>
							{t("drafts.status_reason_dialog.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="px-0.5 pb-4">
						<Select
							value={statusReasonDialog.currentStatusReason || ""}
							onValueChange={(value) => {
								setStatusReasonDialog((prev) => ({
									...prev,
									currentStatusReason: value,
								}));
							}}
						>
							<SelectTrigger>
								<SelectValue
									placeholder={t("drafts.status_reason_dialog.placeholder")}
								/>
							</SelectTrigger>
							<SelectContent>
								{globalProps?.statusReasons?.map((reason: StatusReason) => (
									<SelectItem key={reason.value} value={reason.value}>
										{reasonLabel(reason, lng)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setStatusReasonDialog({
									isOpen: false,
									draftId: null,
									currentStatusReason: null,
								});
							}}
						>
							{t("drafts.status_reason_dialog.cancel")}
						</Button>
						<Button
							onClick={() => {
								if (statusReasonDialog.currentStatusReason) {
									handleConfirmStatusReasonUpdate(
										statusReasonDialog.currentStatusReason,
									);
								}
							}}
							disabled={!statusReasonDialog.currentStatusReason}
						>
							{t("drafts.status_reason_dialog.update")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={picSlotsDialog.isOpen}
				onOpenChange={(open) => {
					if (!open && !picSlotsDialog.saving) {
						setPicSlotsDialog({
							isOpen: false,
							draftId: null,
							slots: EMPTY_PIC_SLOTS,
							supporting: [],
							saving: false,
						});
					}
				}}
			>
				<DialogContent className="sm:max-w-[560px]">
					<DialogHeader>
						<DialogTitle>
							{t("drafts.pic_slots_dialog.title", {
								defaultValue: "Ubah PIC draf",
							})}
						</DialogTitle>
						<DialogDescription>
							{t("drafts.pic_slots_dialog.description", {
								defaultValue:
									"Menugaskan seseorang ke sebuah slot langsung memberinya akses ke draf ini. Melepas seseorang dari slot mencabut aksesnya, kecuali ia masih terdaftar sebagai PIC pendukung.",
							})}
						</DialogDescription>
					</DialogHeader>

					<div className="px-0.5 pb-4">
						<Deferred
							data={["admins"]}
							fallback={<Skeleton className="w-full h-20 rounded-md" />}
						>
							<PicSlotSelectors
								className="md:grid-cols-1"
								admins={(globalProps.admins || []).map((admin) => ({
									id: String(admin.id),
									name: admin.name,
									email: admin.user?.email,
								}))}
								value={picSlotsDialog.slots}
								disabled={picSlotsDialog.saving}
								onChange={(slots) =>
									setPicSlotsDialog((prev) => ({
										...prev,
										slots,
										// Somebody promoted into a slot leaves the supporting list
										// — they are on the Draft through the slot now, and one
										// person must not read as two.
										supporting: prev.supporting.filter(
											(id) =>
												!holdsPicSlot(
													PIC_SLOT_KEYS.map((key) => slots[key]),
													id,
												),
										),
									}))
								}
							/>

							<div className="mt-4 space-y-1.5">
								<Label className="uppercase">
									{t("drafts.pic_slots_dialog.supporting_label", {
										defaultValue: "PIC pendukung",
									})}
								</Label>

								<MultiSelector
									values={picSlotsDialog.supporting}
									onValuesChange={(supporting) =>
										setPicSlotsDialog((prev) => ({ ...prev, supporting }))
									}
									maxShows={4}
									// The values here are admin ids, because that is what the save
									// sends. Without this the chips would spell those ids back at
									// the admin, who has no way to tell whether they picked the
									// right colleague.
									labelFor={(id) =>
										(globalProps.admins || []).find(
											(admin) => String(admin.id) === id,
										)?.name ?? id
									}
								>
									<MultiSelectorTrigger>
										<MultiSelectorInput
											placeholder={t(
												"drafts.pic_slots_dialog.supporting_placeholder",
												{ defaultValue: "Cari admin…" },
											)}
										/>
									</MultiSelectorTrigger>
									<MultiSelectorContent>
										<MultiSelectorList>
											{(globalProps.admins || [])
												.filter(
													(admin) =>
														!holdsPicSlot(
															PIC_SLOT_KEYS.map(
																(key) => picSlotsDialog.slots[key],
															),
															admin.id,
														),
												)
												.map((admin) => (
													<MultiSelectorItem
														key={admin.id}
														value={String(admin.id)}
													>
														<div className="min-w-0">
															<p className="truncate">{admin.name}</p>
															<p className="text-xs truncate text-muted-foreground">
																{admin.user?.email}
															</p>
														</div>
													</MultiSelectorItem>
												))}
										</MultiSelectorList>
									</MultiSelectorContent>
								</MultiSelector>

								<p className="text-xs text-muted-foreground">
									{t("drafts.pic_slots_dialog.supporting_hint", {
										defaultValue:
											"Selain L1/L2/L3 di atas. Yang bisa membuka draf ini = ketiga slot + daftar ini.",
									})}
								</p>
							</div>
						</Deferred>
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							disabled={picSlotsDialog.saving}
							onClick={() =>
								setPicSlotsDialog({
									isOpen: false,
									draftId: null,
									slots: EMPTY_PIC_SLOTS,
									supporting: [],
									saving: false,
								})
							}
						>
							{t("drafts.status_reason_dialog.cancel")}
						</Button>
						<Button
							onClick={handleConfirmPicSlots}
							disabled={picSlotsDialog.saving || !picSlotsDialog.slots.picL1}
						>
							{t("drafts.status_reason_dialog.update")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={cancelDialog.isOpen}
				onOpenChange={(open) => {
					if (!open) {
						setCancelDialog({ isOpen: false, draftId: null, reason: "" });
					}
				}}
			>
				<DialogContent className="sm:max-w-[480px]">
					<DialogHeader>
						<DialogTitle>{t("drafts.cancel_dialog.title")}</DialogTitle>
						<DialogDescription>
							{t("drafts.cancel_dialog.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="px-0.5 pb-4 min-w-0">
						<Select
							value={cancelDialog.reason}
							onValueChange={(value) =>
								setCancelDialog((prev) => ({ ...prev, reason: value }))
							}
						>
							<SelectTrigger className="w-full [&>span]:min-w-0">
								<SelectValue
									placeholder={t("drafts.cancel_dialog.placeholder")}
								/>
							</SelectTrigger>
							<SelectContent className="max-w-[--radix-select-trigger-width]">
								{(globalProps?.cancelReasons || []).map(
									(reason: StatusReason) => (
										<SelectItem
											key={reason.value}
											value={reason.value}
											className="whitespace-normal"
										>
											{reasonLabel(reason, lng)}
										</SelectItem>
									),
								)}
							</SelectContent>
						</Select>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() =>
								setCancelDialog({ isOpen: false, draftId: null, reason: "" })
							}
						>
							{t("drafts.cancel_dialog.close")}
						</Button>
						<Button
							variant="destructive"
							onClick={handleConfirmCancelDraft}
							disabled={!cancelDialog.reason}
						>
							{t("drafts.cancel_dialog.confirm")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default AppointmentDrafts;
